import { useEffect } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { FormikSwitch } from '../../components/FormikFields';
import type { WorkingHours, WorkingWindow } from '../../services/networks.service';

export type { WorkingHours, WorkingWindow };

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = [1, 2, 3, 4, 5];

export const DAY = 24 * 60;

// The clock lives on the network (shared with the caps), not in here.
export const defaultWorkingHours = (): WorkingHours => ({
  windows: [{ days: WEEKDAYS, start: 9 * 60, end: 17 * 60 }],
});

const STEP = 15;
const label = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
// Values are the stored minutes, labels the clock: nothing converts, nothing can be blank.
const STARTS = Array.from({ length: DAY / STEP }, (_, i) => i * STEP);
const ENDS = [...STARTS.slice(1), DAY];

const backwards = (w: WorkingWindow) => w.end <= w.start;
export const scheduleIsValid = (wh: WorkingHours | null) =>
  !wh || wh.windows.every((w) => w.days.length > 0 && !backwards(w));

export default function WorkingHoursField({
  value, onChange, onNormalise,
}: {
  value: WorkingHours | null;
  onChange: (v: WorkingHours | null) => void;
  onNormalise?: (v: WorkingHours) => void;
}) {
  // A shape the form cannot read must not white-screen the dialog; show the default.
  const wh: WorkingHours | null = value
    ? {
      windows: (Array.isArray(value.windows) && value.windows.length > 0 ? value.windows : defaultWorkingHours().windows)
        .map((w) => ({
          days: (Array.isArray(w?.days) ? w.days : []).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6),
          start: STARTS.includes(w?.start) ? w.start : 9 * 60,
          end: ENDS.includes(w?.end) ? w.end : 17 * 60,
        })),
    }
    : null;
  // What is on screen is what gets saved, even when the stored value needed repairing.
  useEffect(() => {
    if (wh && value && JSON.stringify(wh) !== JSON.stringify(value)) onNormalise?.(wh);
  }, [wh, value, onNormalise]);

  const patch = (p: Partial<WorkingHours>) => onChange({ ...(wh ?? defaultWorkingHours()), ...p });
  const patchWindow = (i: number, p: Partial<WorkingWindow>) =>
    patch({ windows: (wh?.windows ?? []).map((w, j) => (j === i ? { ...w, ...p } : w)) });

  return (
    <Box>
      <FormikSwitch name="working_hours_enabled" label="Only accept traffic during working hours" />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Outside these hours the network's offers are skipped: the click goes to another offer or
        path, and to the global fallback offer only when nothing else can take it.
      </Typography>

      {wh && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Read on the network's timezone (set above), the same clock the daily caps use.
          </Typography>

          {wh.windows.map((w, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {i === 0 ? 'Open' : 'And also open'}
                </Typography>
                <IconButton
                  size="small" aria-label="Remove hours"
                  disabled={wh.windows.length === 1}
                  onClick={() => patch({ windows: wh.windows.filter((_, j) => j !== i) })}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
              <ToggleButtonGroup
                size="small" value={w.days} sx={{ display: 'flex' }}
                onChange={(_, days: number[]) => patchWindow(i, { days })}
              >
                {DAY_LABELS.map((label, day) => (
                  <ToggleButton key={day} value={day} sx={{ flex: 1, py: 0.5 }}>{label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {w.days.length === 0 && (
                <Typography variant="caption" color="error">Pick at least one day.</Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  select size="small" label="From" value={w.start} sx={{ flex: 1 }}
                  onChange={(e) => patchWindow(i, { start: Number(e.target.value) })}
                >
                  {STARTS.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="To" value={w.end} sx={{ flex: 1 }}
                  onChange={(e) => patchWindow(i, { end: Number(e.target.value) })}
                >
                  {ENDS.map((m) => (
                    <MenuItem key={m} value={m}>{m === DAY ? '24:00 (end of day)' : label(m)}</MenuItem>
                  ))}
                </TextField>
              </Box>
              {backwards(w) ? (
                <Typography variant="caption" color="error">
                  Hours must end after they start. For a night shift add a second set on the
                  next day — 22:00 to end of day, then 00:00 to 02:00.
                </Typography>
              ) : null}
            </Box>
          ))}

          <Button
            size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start' }}
            onClick={() => patch({ windows: [...wh.windows, { days: [6], start: 10 * 60, end: 14 * 60 }] })}
          >
            Add different hours
          </Button>
        </Box>
      )}
    </Box>
  );
}
